// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building's spatial structure from the server — `GET /storeys`,
 * `GET /rooms` and `InstanceInfo.centroid/bbox` (ruxd-6tz, CONTRACT-GAPS
 * gaps 6, 7, 8) — in the shapes the Ground already uses for what it folds
 * from the streamed pages (`viewport/instanceIndex`), and the rules for
 * merging the two:
 *
 *  - the server's figures are computed over the whole cloud, so they win;
 *    the streamed ones fill in whatever the server did not answer (an older
 *    server without the routes, a 404, an instance with a null centroid);
 *  - stored storeys win over the height heuristic (`deriveStoreys`) as soon
 *    as the server has any; with none stored (or no route) the heuristic
 *    stays, so a project without the Etager stage still gets its levels.
 *
 * With the server's answer the breadcrumb, storey/room focus, labels and
 * click targets work before the cloud has streamed. Pure: no React, no DOM.
 */

import type { InstanceInfo, Room, SolidSpace, Storey as ApiStorey, SurveyType, WorldBox, WorldPoint } from '../api/types';
import type { ExtentStats, InstanceStats, Vec3 } from '../viewport/instanceIndex';
import type { Storey } from './storeys';

function vec(p: WorldPoint): Vec3 {
  return { x: p[0], y: p[1], z: p[2] };
}

function finitePoint(p: unknown): p is WorldPoint {
  return Array.isArray(p) && p.length === 3 && p.every((v) => typeof v === 'number' && Number.isFinite(v));
}

function finiteBox(b: unknown): b is WorldBox {
  return Array.isArray(b) && b.length === 2 && finitePoint(b[0]) && finitePoint(b[1]);
}

/** The stored storeys as the shell's `Storey`, lowest first (fewer than two is no hierarchy: `[]`). */
export function storeysFromServer(list: readonly ApiStorey[]): Storey[] {
  if (list.length < 2) return [];
  return [...list]
    .sort((a, b) => a.z_min - b.z_min || a.id - b.id)
    .map((s) => ({
      id: s.id,
      name: s.name,
      zMin: s.z_min,
      zMax: s.z_max,
      roomIds: [...s.room_ids].sort((a, b) => a - b),
      ...(s.is_ground ? { isGround: true } : {}),
    }));
}

/**
 * The storeys to show: the server's when it has stored any (even a single
 * one, which is no hierarchy), else `derive()` — the heuristic over the
 * streamed room centroids. `server` is null when the route is missing or
 * failed.
 */
export function chooseStoreys(server: readonly ApiStorey[] | null, derive: () => Storey[]): Storey[] {
  if (server && server.length > 0) return storeysFromServer(server);
  return derive();
}

/** `GET /rooms` as extents by room id (rooms without a finite box are skipped). */
export function roomExtents(rooms: readonly Room[]): Map<number, ExtentStats> {
  const out = new Map<number, ExtentStats>();
  for (const r of rooms) {
    if (!finitePoint(r.centroid) || !finiteBox(r.bbox)) continue;
    out.set(r.id, { id: r.id, count: r.point_count, centroid: vec(r.centroid), min: vec(r.bbox[0]), max: vec(r.bbox[1]) });
  }
  return out;
}

/** Instance id → the room its part is filed in (`SurveyPart.room_id`), for the parts of `cloud`. */
export function partRooms(types: readonly SurveyType[], cloud: string): Map<number, number> {
  const out = new Map<number, number>();
  for (const t of types) {
    for (const p of t.parts) {
      if (p.cloud === cloud && p.instance_id !== null && p.room_id !== null) out.set(p.instance_id, p.room_id);
    }
  }
  return out;
}

/**
 * `InstanceInfo` rows with a centroid as `InstanceStats` by id. The room is
 * the part's (`rooms`, from `partRooms`); the per-room tally holds only that.
 */
export function instanceExtents(list: readonly InstanceInfo[], rooms: ReadonlyMap<number, number>): Map<number, InstanceStats> {
  const out = new Map<number, InstanceStats>();
  for (const i of list) {
    if (!finitePoint(i.centroid)) continue;
    const c = vec(i.centroid);
    const box = finiteBox(i.bbox) ? i.bbox : null;
    const room = rooms.get(i.instance_id) ?? null;
    out.set(i.instance_id, {
      id: i.instance_id,
      count: i.point_count,
      centroid: c,
      min: box ? vec(box[0]) : c,
      max: box ? vec(box[1]) : c,
      room,
      rooms: room !== null ? new Map([[room, i.point_count]]) : new Map(),
    });
  }
  return out;
}

/**
 * Room extents: the server's where it has the room, the streamed ones for
 * the rest. Returns `streamed` itself when the server has nothing.
 */
export function mergeRoomExtents(
  server: ReadonlyMap<number, ExtentStats> | null,
  streamed: Map<number, ExtentStats>,
): Map<number, ExtentStats> {
  if (!server || server.size === 0) return streamed;
  const out = new Map(streamed);
  for (const [id, s] of server) out.set(id, s);
  return out;
}

/**
 * Instance stats: the server's centroid, box and count where it has the
 * instance — keeping the streamed room tally when there is one, since it
 * counts the points per room — the streamed stats for the rest. Returns
 * `streamed` itself when the server has nothing.
 */
export function mergeInstanceExtents(
  server: ReadonlyMap<number, InstanceStats> | null,
  streamed: Map<number, InstanceStats>,
): Map<number, InstanceStats> {
  if (!server || server.size === 0) return streamed;
  const out = new Map(streamed);
  for (const [id, s] of server) {
    const seen = streamed.get(id);
    out.set(id, seen && seen.room !== null ? { ...s, room: seen.room, rooms: seen.rooms } : s);
  }
  return out;
}

/** `GET /spaces` rows on `storeyId` (its own stored id); every space when `storeyId` is null ("hele bygningen"). */
export function spacesOnStorey(spaces: readonly SolidSpace[], storeyId: number | null): SolidSpace[] {
  return storeyId === null ? [...spaces] : spaces.filter((s) => s.storey_id === storeyId);
}

/** The "N rum" to show for a storey or the whole building: the building model's spaces when in scope, else the scan's rooms. */
export interface RoomsOrSpaces {
  /** `'spaces'` when `GET /spaces` has entries in scope; else `'rooms'` (`fallback` unchanged). */
  source: 'spaces' | 'rooms';
  count: number;
  /** Names to list, in `spaces`' order; only set for `source: 'spaces'` — a `'rooms'` caller keeps its own `RoomInfo[]`. */
  names: string[];
}

/**
 * Prefers the building model's spaces (`GET /spaces`, `space.name`) over the
 * scan's rooms cloud for "N rum": an imported Revit/IFC case has no rooms
 * cloud, so `fallback` (the scan-derived count/names) would always read "0
 * rum" there. Scoped to `storeyId` (its own stored id, matching
 * `SolidSpace.storey_id`), or the whole building when `storeyId` is null.
 * Falls back to `fallback` when the server has no spaces, or none in scope
 * (an older server, a scan-only project, or a storey the import didn't
 * model).
 */
export function roomsOrSpaces(
  spaces: readonly SolidSpace[] | null,
  storeyId: number | null,
  fallback: { count: number; names: readonly string[] },
): RoomsOrSpaces {
  const inScope = spaces ? spacesOnStorey(spaces, storeyId) : [];
  if (inScope.length === 0) return { source: 'rooms', count: fallback.count, names: [...fallback.names] };
  return { source: 'spaces', count: inScope.length, names: inScope.map((s) => s.name) };
}

/** Centroids for `ids` as xyz stride 3; `NaN` for an id without stats (as `InstanceIndex.centroids`). */
export function centroidsOf(stats: ReadonlyMap<number, ExtentStats>, ids: readonly number[]): Float32Array {
  const out = new Float32Array(ids.length * 3).fill(Number.NaN);
  ids.forEach((id, i) => {
    const s = stats.get(id);
    if (!s) return;
    out[i * 3] = s.centroid.x;
    out[i * 3 + 1] = s.centroid.y;
    out[i * 3 + 2] = s.centroid.z;
  });
  return out;
}
